<template>
  <ChatUi
      :session="session"
      :connectionId="connectionId"
      :pendingMessage="pendingMessage"
      :pendingHashhashDomain="pendingHashhashDomain"
      :pendingClientAccountName="pendingClientAccountName"
      :embedMode="embedMode"
      :domainAccount="domainAccount"
      :unverified="unverified"
      @show-login="showLogin = true"
      @logout="onLogout"
      @show-change-password="showChangePassword = true"
      @pending-sent="onPendingSent"
      @temp-started="onTempStarted"
      @verified="onVerified"
  />

  <ChatLogin
      v-if="showLogin"
      @logged-in="onLogin"
      @close="showLogin = false"
  />

  <ChatChangePassword
      v-if="showChangePassword"
      :domain="changePasswordDomain"
      :forced="changePasswordForced"
      :initialOldPassword="tempPassword"
      @changed="onPasswordChanged"
      @cancel="showChangePassword = false"
      @logout="onLogout"
  />
</template>

<script>
import ChatUi from "./components/ChatUi.vue";
import ChatLogin from "./components/ChatLogin.vue";
import ChatChangePassword from "./components/ChatChangePassword.vue";
import ChatApiService from "./services/ChatApiService.js";

export default {
  name: "App",
  components: {
    ChatLogin,
    ChatChangePassword,
    ChatUi
  },

  props: {
    embedMode: { type: Boolean, default: false }
  },

  data() {
    const getRawParam = (name) => {
      const m = window.location.href.match(new RegExp(`[?&]${name}=([^&]+)`));
      return m ? decodeURIComponent(m[1]) : "";
    };
    // Gast-Einbettung (embed=1&tenant=…) laeuft im Rahmen einer fremden Seite: deren URL darf weder eine
    // Verbindung waehlen noch Text vorbelegen oder einen Chat starten (Clickjacking, Review 2026-10-23).
    const guestEmbed = new URLSearchParams(window.location.search).get('embed') === '1' && !!getRawParam('tenant');
    const hostParam = (name) => (guestEmbed ? "" : getRawParam(name));
    const rawChattext = hostParam('chattext');
    let prefilled = "";
    if (rawChattext) {
      try {
        const binary = atob(rawChattext);
        const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
        prefilled = new TextDecoder('utf-8').decode(bytes);
      } catch (e) { /* ignore */ }
    }
    const hashhashDomain = hostParam('hashhashDomain');
    const clientAccountName = hostParam('clientAccountName');
    const hasPending = prefilled && hashhashDomain && clientAccountName;
    return {
      showLogin: false,
      session: null,
      connectionId: hostParam('connectionId') || null,
      // Vorausgefüllter Composer-Text. Wird sowohl im "Start-Chat-zu-Client"-
      // Flow (mit hashhashDomain+clientAccountName) als auch beim direkten
      // Öffnen einer bestehenden Connection (connectionId) genutzt.
      pendingMessage: prefilled,
      pendingHashhashDomain: hasPending ? hashhashDomain : "",
      pendingClientAccountName: hasPending ? clientAccountName : "",
      showChangePassword: false,
      changePasswordForced: false,
      tempPassword: "",
      // Anonymous onboarding (embed only): tenant is already "account##domain".
      domainAccount: getRawParam('tenant'),
      anonId: "",
      unverified: false
    };
  },

  computed: {
    changePasswordDomain() {
      return this.session?.domain || 'tibtix.com';
    }
  },

  async mounted() {
    ChatApiService.onUnauthorized(() => {
      this.session = null;
      this.showLogin = true;
    });

    const session = await ChatApiService.checkLogin();
    if (session) {
      this.session = session;
    } else if (this.embedMode && this.domainAccount) {
      // Embedded with a known company and nobody logged in: allow anonymous chatting.
      // No forced login overlay — ChatUi renders the guest composer.
    } else {
      this.showLogin = true;
    }
  },

  methods: {
    // Anonymous visitor sent the first message -> a temp chat + anon client account exist.
    // Refresh the session (the visitor is now an anonymous client) and hand the connectionId
    // to ChatUi so init() auto-selects the new connection. Stay in the unverified state.
    async onTempStarted({ connectionId, anonId }) {
      this.anonId = anonId;
      const session = await ChatApiService.checkLogin();
      if (session) {
        this.unverified = true;
        this.connectionId = connectionId;
        this.session = session;
      }
    },

    // Email address verified inside ChatUi (code login + claim done). The session is now the
    // real email account; connectionId is unchanged. Drop the unverified state -> full UI.
    async onVerified() {
      const session = await ChatApiService.checkLogin();
      if (session) {
        this.unverified = false;
        this.session = session;
      }
      this.anonId = "";
    },

    async onLogin(session) {
      // If the visitor had an anonymous chat going, attach it to the account they just
      // logged into (claim AFTER login; connectionId stays the same).
      if (this.anonId) {
        try {
          await new ChatApiService('default', {}).claimTempChat(this.anonId);
        } catch (e) { /* best-effort */ }
        this.anonId = "";
        this.unverified = false;
      }
      this.session = session;
      this.showLogin = false;
      if (session.pendingMessage && session.pendingHashhashDomain && session.pendingClientAccountName) {
        this.pendingMessage = session.pendingMessage;
        this.pendingHashhashDomain = session.pendingHashhashDomain;
        this.pendingClientAccountName = session.pendingClientAccountName;
      }
      if (session.loggedInWithTempPassword) {
        this.tempPassword = session.password || "";
        this.changePasswordForced = true;
        this.showChangePassword = true;
      }
    },

    onPasswordChanged() {
      this.showChangePassword = false;
      this.changePasswordForced = false;
      this.tempPassword = "";
      if (this.session) this.session.loggedInWithTempPassword = false;
    },

    onLogout() {
      this.session = null;
      this.unverified = false;
      this.anonId = "";
      this.connectionId = null;
      this.showChangePassword = false;
      this.changePasswordForced = false;
      this.tempPassword = "";
      // In the embed, a logged-out visitor can chat anonymously again instead of
      // being forced to log in.
      this.showLogin = !(this.embedMode && this.domainAccount);
      fetch('/tt/api/logout', { method: 'GET', credentials: 'include' }).catch(() => {});
    },

    onPendingSent() {
      this.pendingMessage = "";
      this.pendingHashhashDomain = "";
      this.pendingClientAccountName = "";
      const cleaned = window.location.href
          .replace(/([?&])chattext=[^&]*/g, '$1')
          .replace(/([?&])hashhashDomain=[^&]*/g, '$1')
          .replace(/([?&])clientAccountName=[^&]*/g, '$1')
          .replace(/\?&+/g, '?')
          .replace(/&+/g, '&')
          .replace(/[?&]$/, '');
      window.history.replaceState({}, '', cleaned);
    }
  }
};
</script>
